import { FC, UIEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
	ArrowSmUpOutline,
	Button,
	Center,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	rem,
	Text,
	useBreakpointValue,
} from '@bonusly/design-system';

import { AwardType, Bonus } from '../../../../../types/bonus';
import { getBonusType } from '../../../../../utils/bonus';
import { AppPath } from '../../config';
import { BonusCardSkeleton } from '../bonus/bonus-card-skeleton';
import { BonusModalContent } from './components';

type BonusModalProps = {
	bonus?: Bonus;
	commentsDefaultOpen?: boolean;
	handleCloseModal: () => void;
	isLoading: boolean;
};

export const BonusModal: FC<BonusModalProps> = ({ bonus, commentsDefaultOpen, handleCloseModal, isLoading }) => {
	const modalRef = useRef<HTMLDivElement | null>(null);
	const commentBoxRef = useRef<HTMLDivElement | null>(null);

	const navigate = useNavigate();
	const isAtLeastSm = useBreakpointValue({ base: false, sm: true });
	const location = useLocation();
	const { t } = useTranslation('common');

	const [isScrollToTopButtonVisible, setIsScrollToTopButtonVisible] = useState(false);

	const handleOnScroll: UIEventHandler<HTMLDivElement> = ({ target }) => {
		const { scrollTop, clientHeight }: HTMLDivElement = target as HTMLDivElement;

		setIsScrollToTopButtonVisible(scrollTop > clientHeight * 4);
	};

	const handleScrollToTop = () => {
		modalRef.current?.scrollTo(0, 0);
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const type = params.get('type');
		if (type === 'say-thanks' && !isLoading) {
			setTimeout(() => {
				commentBoxRef?.current?.scrollIntoView({ behavior: 'smooth' });
			}, 100);
		} else {
			setTimeout(() => {
				modalRef?.current?.scrollTo(0, 0);
			}, 100);
		}
	}, [bonus?.id, isLoading, location.search]);

	const modalHeader = useMemo(() => {
		if (isLoading || !bonus) return null;
		const awardType = bonus.award_type;

		// NOTE: if awardType is any celebration use awards.types.celebration
		if (getBonusType(bonus) === 'celebration') {
			return t('awards.types.celebration', { ns: 'recognition' });
		}

		// NOTE: if awardType is claimable use awards.types.incentives
		if (awardType === AwardType.Claimable) {
			return t('awards.types.incentives', { ns: 'recognition' });
		}

		return t(`awards.types.${awardType ? 'general' : 'recognition'}`, {
			ns: 'recognition',
		});
	}, [bonus, isLoading, t]);

	const modalContent = useMemo(() => {
		if (isLoading) {
			return <BonusCardSkeleton />;
		}
		if (bonus) {
			return <BonusModalContent commentsDefaultOpen={commentsDefaultOpen} commentBoxRef={commentBoxRef} bonus={bonus} />;
		}
		navigate(AppPath.NotFound, { replace: true });
	}, [bonus, isLoading, navigate, commentsDefaultOpen]);

	const backToTopButton = isScrollToTopButtonVisible ? (
		<Center flexDir="column" width="100%" mt={-8} mb={2}>
			<Button
				colorScheme="secondary"
				variant="solid"
				onClick={handleScrollToTop}
				style={{
					zIndex: '9999',
				}}
				data-testid={`modal-recognition-back-to-top`}
			>
				<ArrowSmUpOutline />
				{t('back_to_top', {
					ns: 'common',
				})}
			</Button>
		</Center>
	) : null;

	return (
		<Modal
			data-testid={`modal-recognition-${bonus?.id}`}
			isOpen={!!bonus?.id}
			onClose={handleCloseModal}
			scrollBehavior="inside"
			size={{ base: 'full', md: 'xl' }}
		>
			<ModalOverlay />
			<ModalContent
				maxH="calc(100% - 7.5rem)"
				maxW={rem(684)}
				top={isAtLeastSm ? 5 : 'auto'}
				{...(!isAtLeastSm && {
					height: 'auto',
					maxHeight: '100%',
					borderRadius: 'none',
					maxH: 'auto',
					my: 0,
				})}
			>
				<ModalHeader color="text.default" px={rem(24)} pt={rem(16)} pb={rem(12)} fontSize="xl" fontWeight="semibold" borderBottomWidth={0}>
					<HStack justify="space-between" w="100%">
						<Text as="h2" fontSize="xl" fontWeight="semibold" lineHeight={8}>
							{modalHeader}
						</Text>
						<ModalCloseButton h={rem(32)} w={rem(32)} pos="static" borderRadius="full" />
					</HStack>
				</ModalHeader>
				<ModalBody onScroll={handleOnScroll} ref={modalRef} position={'relative'} p={0}>
					{modalContent}
				</ModalBody>
				{backToTopButton}
			</ModalContent>
		</Modal>
	);
};
